import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { getPortalRoot } from '../../utils/portal';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  containerClassName?: string;
  noPadding?: boolean; // Permet de désactiver le padding par défaut
}

export function Modal({ isOpen, onClose, title, children, size = 'md', containerClassName, noPadding = false }: ModalProps) {
  // Empêcher le scroll du body quand la modale est ouverte
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Fermer sur Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl',
  }[size];

  // Utiliser createPortal pour rendre le modal en dehors du flux DOM normal
  // Cela évite les problèmes de overflow:hidden et z-index des parents
  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {/* Overlay - fond semi-transparent */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
        aria-hidden="true"
      /> 
      
      {/* Conteneur centré avec scroll si nécessaire */}
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
          {/* Contenu du modal - empêche la propagation du clic */}
          <div 
            className={`
              relative ${sizeClasses} w-full 
              bg-card text-card-foreground 
              rounded-lg shadow-xl border border-border
              transform transition-all
              max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)]
              flex flex-col
              ${containerClassName || ''}
            `}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - fixe */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
              <h2 id="modal-title" className="text-lg font-semibold text-foreground">{title}</h2>
              <button
                onClick={onClose}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Body - scrollable */}
            <div className={`flex-1 overflow-y-auto ${noPadding ? '' : 'p-6'}`}> 
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Rendre le modal dans un portal racine dédié au lieu d'utiliser document.body directement
  return createPortal(modalContent, getPortalRoot());
}